import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import {
    createChartExportHandlers,
    createMenuToggle,
    setupMenuClickOutside,
    exportToCSV,
} from '../../utils/exportUtils'
import './HuddleNextHuddle.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

const Q10_QUESTION =
    'Did the team discuss expectations for when the next huddle may occur?'
const Q11_QUESTION =
    'Were the expectations for when the next huddle may occur written on the board?'

const Q10_ANSWER_ORDER: string[] = ['Yes', 'No']
const Q10_ANSWER_COLORS: Record<string, string> = {
    Yes: '#2C5985',
    No: '#E48E43',
}
const Q10_DATA_LABEL_COLORS: Record<string, string> = {
    Yes: '#ffffff',
    No: '#ffffff',
}

const Q11_ANSWER_ORDER: string[] = ['Yes', 'Yes, Previously', 'No']
const Q11_ANSWER_COLORS: Record<string, string> = {
    Yes: '#2C5985',
    'Yes, Previously': '#AEDCD5',
    No: '#E48E43',
}
const Q11_DATA_LABEL_COLORS: Record<string, string> = {
    Yes: '#ffffff',
    'Yes, Previously': '#626262',
    No: '#ffffff',
}

const SHARED_LEGEND_ORDER: string[] = ['No', 'Yes, Previously', 'Yes']
const SHARED_LEGEND_COLORS: Record<string, string> = {
    No: '#E48E43',
    'Yes, Previously': '#AEDCD5',
    Yes: '#2C5985',
}

const SECTION_MENU_ID = 'hdl-next-huddle-section-export-menu'
const HDL_SLIDER_KEY = 'hdl_slider'

const largestRemainderRound = (rawPcts: number[]): number[] => {
    const floors = rawPcts.map(p => Math.floor(p))
    const rem = 100 - floors.reduce((s, f) => s + f, 0)
    const diffs = rawPcts.map((p, i) => ({ i, diff: p - floors[i] })).sort((a, b) => b.diff - a.diff)
    const adj = [...floors]
    for (let k = 0; k < Math.max(0, rem) && k < diffs.length; k++) adj[diffs[k].i] += 1
    return adj
}

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const normalizeQ10Answer = (raw: string): string | null => {
    const lower = raw.trim().toLowerCase()
    if (!lower) return null
    if (lower === 'yes') return 'Yes'
    if (lower === 'no') return 'No'
    return null
}

const normalizeQ11Answer = (raw: string): string | null => {
    const lower = raw.trim().toLowerCase()
    if (!lower) return null
    if (lower.includes('previously')) return 'Yes, Previously'
    if (lower === 'yes') return 'Yes'
    if (lower === 'no') return 'No'
    return null
}

const HuddleNextHuddle: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data: q10Data, loading: q10Loading, initialLoading: q10InitialLoading, error: q10Error } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_expectation_q10',
        hospitalIds
    )
    const { data: q11Data, loading: q11Loading, initialLoading: q11InitialLoading, error: q11Error } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_expectation_written_q11',
        hospitalIds
    )

    const loading = q10Loading || q11Loading
    const initialLoading = q10InitialLoading || q11InitialLoading
    const error = q10Error || q11Error

    const sectionRef = useRef<HTMLDivElement>(null)
    const sectionExports = useMemo(
        () => createChartExportHandlers(
            sectionRef,
            'Discussion & Board Use: Next Huddle — Q10 & Q11',
            'huddle_next_huddle_q10_q11',
            { fontBoost: true, exportTitle: 'Discussion & Board Use: Next Huddle', exportTitleAlign: 'center', exportTitleFontSize: '28px', extraHideSelectors: ['.stacked-bar-chart-legend', '.chart-loading-overlay'] }
        ),
        []
    )
    const toggleSectionMenu = useMemo(() => createMenuToggle(SECTION_MENU_ID), [])

    useEffect(() => {
        return setupMenuClickOutside(() => sectionExports.closeMenu(SECTION_MENU_ID))
    }, [sectionExports])

    const q10Rows: any[][] = q10Data?.response?.result?.rows || []
    const q11Rows: any[][] = q11Data?.response?.result?.rows || []

    const monthList: MonthInfo[] = useMemo(() => {
        const combined = [...q10Rows, ...q11Rows]
        if (!combined.length) return []
        return generateMonthList(combined, 2)
    }, [q10Rows, q11Rows])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            try {
                const item = sessionStorage.getItem(persistenceKey)
                const parsed = item !== null ? JSON.parse(item) : null
                userInteractedRef.current =
                    Array.isArray(parsed) && parsed[0] !== 0 && parsed[1] !== 0
            } catch {
                userInteractedRef.current = false
            }
        } else {
            userInteractedRef.current = false
        }
    }

    useEffect(() => {
        if (!monthList.length) return
        if (loading) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, loading, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    type HospitalAggregate = {
        id: string
        name: string
        counts: Record<string, number>
        total: number
        percentages: Record<string, number>
    }

    type Aggregated = {
        counts: Record<string, number>
        total: number
        totalN: number
        percentages: Record<string, number>
        hospitalCount: number
        perHospital: HospitalAggregate[]
    }

    // Q10 columns: hospital_id | hospital_name | month | question_text | response | numerator | denominator | percentage | n_count
    // Q11 columns: hospital_id | hospital_name | month | question_text | response | numerator | denominator | percentage | n_count
    // question_text is informational only (both questions render as a single bar).
    const aggregateRows = (
        sourceRows: any[][],
        answerOrder: string[],
        normalize: (raw: string) => string | null,
        responseIdx: number,
        numeratorIdx: number,
        denominatorIdx: number,
        nCountIdx: number,
    ): Aggregated => {
        const empty: Aggregated = { counts: {}, total: 0, totalN: 0, percentages: {}, hospitalCount: 0, perHospital: [] }
        if (!monthList.length || !sourceRows.length) return empty

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        const perHospital: Record<string, {
            id: string; name: string;
            counts: Record<string, number>; total: number;
        }> = {}
        const seenGroups = new Set<string>()
        let totalN = 0

        sourceRows.forEach(r => {
            const hospId = String(r[0])
            const hospName = String(r[1] || '').trim()
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const answer = normalize(String(r[responseIdx] || ''))
            if (!answer) return
            const numerator = Number(r[numeratorIdx]) || 0
            const denom = Number(r[denominatorIdx]) || 0
            const nCount = Number(r[nCountIdx]) || 0

            if (!perHospital[hospId]) perHospital[hospId] = { id: hospId, name: hospName, counts: {}, total: 0 }
            const h = perHospital[hospId]
            if (!h.name && hospName) h.name = hospName
            h.counts[answer] = (h.counts[answer] || 0) + numerator

            const groupKey = `${hospId}|${monthVal}`
            if (!seenGroups.has(groupKey)) {
                seenGroups.add(groupKey)
                h.total += denom
                totalN += nCount
            }
        })

        const contributing = Object.values(perHospital).filter(h => h.total > 0)
        if (contributing.length === 0) {
            return { counts: {}, total: 0, totalN, percentages: {}, hospitalCount: 0, perHospital: [] }
        }

        const counts: Record<string, number> = {}
        let total = 0
        for (const h of contributing) {
            for (const [a, c] of Object.entries(h.counts)) counts[a] = (counts[a] || 0) + c
            total += h.total
        }

        const percentages: Record<string, number> = {}
        for (const a of answerOrder) {
            percentages[a] = total > 0 ? ((counts[a] || 0) / total) * 100 : 0
        }

        const perHospitalSorted: HospitalAggregate[] = contributing
            .slice()
            .sort((a, b) => a.id.localeCompare(b.id))
            .map(h => {
                const pct: Record<string, number> = {}
                for (const a of answerOrder) {
                    pct[a] = h.total > 0 ? ((h.counts[a] || 0) / h.total) * 100 : 0
                }
                return { id: h.id, name: h.name, counts: { ...h.counts }, total: h.total, percentages: pct }
            })

        return { counts, total, totalN, percentages, hospitalCount: contributing.length, perHospital: perHospitalSorted }
    }

    const q10Aggregated = useMemo(
        () => aggregateRows(q10Rows, Q10_ANSWER_ORDER, normalizeQ10Answer, 4, 5, 6, 8),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [q10Rows, monthList, startIdx, endIdx, isMultiSelect]
    )
    const q11Aggregated = useMemo(
        () => aggregateRows(q11Rows, Q11_ANSWER_ORDER, normalizeQ11Answer, 4, 5, 6, 8),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [q11Rows, monthList, startIdx, endIdx, isMultiSelect]
    )

    const q10HasData = q10Aggregated.total > 0
    const q11HasData = q11Aggregated.total > 0

    const buildChartData = (
        agg: Aggregated,
        answerOrder: string[],
        colorMap: Record<string, string>,
        labelColorMap: Record<string, string>
    ) => {
        if (agg.total <= 0) {
            const first = answerOrder[0]
            return {
                series: [{ name: first, data: [0] }],
                colors: [colorMap[first]],
                dataLabelColors: [labelColorMap[first]],
            }
        }
        const adjMap: Record<string, number> = {}
        answerOrder.forEach(a => { adjMap[a] = Math.round(agg.percentages[a] || 0) })
        const visible = answerOrder.filter(a => adjMap[a] > 0)
        return {
            series: visible.map(a => ({ name: a, data: [adjMap[a]] })),
            colors: visible.map(a => colorMap[a]),
            dataLabelColors: visible.map(a => labelColorMap[a]),
        }
    }

    const q10Chart = useMemo(
        () => buildChartData(q10Aggregated, Q10_ANSWER_ORDER, Q10_ANSWER_COLORS, Q10_DATA_LABEL_COLORS),
        [q10Aggregated]
    )
    const q11Chart = useMemo(
        () => buildChartData(q11Aggregated, Q11_ANSWER_ORDER, Q11_ANSWER_COLORS, Q11_DATA_LABEL_COLORS),
        [q11Aggregated]
    )

    const getMonthRangeLabel = (): string => {
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        return startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''
    }

    const handleDownloadCSV = () => {
        const hospitalIdsStr = hospitalIds.join(',')
        const monthRange = getMonthRangeLabel()
        const rowsOut: (string | number)[][] = []

        const pushBlock = (
            agg: Aggregated,
            question: string,
            answerOrder: string[],
        ) => {
            const rounded = answerOrder.map(a =>
                agg.hospitalCount > 0 ? Math.round(agg.percentages[a] || 0) : 0
            )

            answerOrder.forEach((answer, i) => {
                const count = agg.counts[answer] || 0
                const percent = agg.hospitalCount > 0 ? `${rounded[i]}%` : ''
                rowsOut.push([hospitalIdsStr, monthRange, question, answer, count, percent])
            })
            rowsOut.push([hospitalIdsStr, monthRange, question, 'Total (N)', agg.totalN || 0, ''])
        }

        pushBlock(q10Aggregated, Q10_QUESTION, Q10_ANSWER_ORDER)
        pushBlock(q11Aggregated, Q11_QUESTION, Q11_ANSWER_ORDER)

        exportToCSV({
            headers: ['Hospital ID(s)', 'Month Range', 'Question', 'Answer', 'Count', 'Percentage'],
            rows: rowsOut,
            filename: 'huddle_next_huddle_q10_q11',
        })
    }

    const showInitialLoading = initialLoading && loading
    const isRefreshing = !initialLoading && loading
    const hasHospitals = hospitalIds.length > 0
    const noData = !initialLoading && !loading && !monthList.length

    return (
        <div className="hdl-next-huddle">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Discussion &amp; Board Use: Next Huddle</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !error && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading next huddle data…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && error && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {error}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !error && noData} />

                {hasHospitals && !error && !noData && (
                    <div className="hdl-next-huddle-section" ref={sectionRef}>
                        <div className="hdl-next-huddle-section-head-row">
                            <div className="hdl-next-huddle-section-title">Q10 &amp; Q11</div>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleSectionMenu}>&#9776;</button>
                                <div id={SECTION_MENU_ID} className="export-dropdown">
                                    <div onClick={() => sectionExports.handleDownloadSVG(SECTION_MENU_ID)}>Download SVG</div>
                                    <div onClick={() => sectionExports.handleDownloadPNG(SECTION_MENU_ID)}>Download PNG</div>
                                    <div onClick={() => { handleDownloadCSV(); sectionExports.closeMenu(SECTION_MENU_ID); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>

                        <div className="hdl-next-huddle-charts">
                            <div className="hdl-next-huddle-question">{Q10_QUESTION}</div>
                            <div className="hdl-next-huddle-question">{Q11_QUESTION}</div>
                            <div className="hdl-next-huddle-legend-spacer" aria-hidden="true" />

                            {q10HasData ? (
                                <div className="hdl-next-huddle-chart-stack">
                                    <StackedBarChart
                                        categories={['']}
                                        series={q10Chart.series}
                                        colors={q10Chart.colors}
                                        height={480}
                                        columnWidth="100%"
                                        fontFamily="Poppins, 'Segoe UI', sans-serif"
                                        yAxisShow={false}
                                        xAxisShow={false}
                                        showGrid={false}
                                        sparkline={true}
                                        showDataLabels={true}
                                        dataLabelColors={q10Chart.dataLabelColors}
                                        dataLabelMinPercent={8}
                                        dataLabelFontSize="14px"
                                        fillOpacity={1}
                                        exportFilename="huddle_next_huddle_q10"
                                    />
                                </div>
                            ) : (
                                <div className="hdl-next-huddle-empty">No data for the selected range</div>
                            )}

                            {q11HasData ? (
                                <div className="hdl-next-huddle-chart-stack">
                                    <StackedBarChart
                                        categories={['']}
                                        series={q11Chart.series}
                                        colors={q11Chart.colors}
                                        height={480}
                                        columnWidth="100%"
                                        fontFamily="Poppins, 'Segoe UI', sans-serif"
                                        yAxisShow={false}
                                        xAxisShow={false}
                                        showGrid={false}
                                        sparkline={true}
                                        showDataLabels={true}
                                        dataLabelColors={q11Chart.dataLabelColors}
                                        dataLabelMinPercent={8}
                                        dataLabelFontSize="14px"
                                        fillOpacity={1}
                                        exportFilename="huddle_next_huddle_q11"
                                    />
                                </div>
                            ) : (
                                <div className="hdl-next-huddle-empty">No data for the selected range</div>
                            )}

                            <div className="hdl-next-huddle-legend">
                                {SHARED_LEGEND_ORDER.map(answer => (
                                    <div key={answer} className="hdl-next-huddle-legend-item">
                                        <span
                                            className="hdl-next-huddle-legend-dot"
                                            style={{ backgroundColor: SHARED_LEGEND_COLORS[answer] }}
                                        />
                                        <span className="hdl-next-huddle-legend-label">{answer}</span>
                                    </div>
                                ))}
                            </div>

                            {(q10HasData || q11HasData) && (
                                <div className="hdl-next-huddle-n-row">
                                    <span className="hdl-next-huddle-n-label">N=</span>
                                    <span className="hdl-next-huddle-bar-n">{q10HasData ? q10Aggregated.totalN : ''}</span>
                                    <span className="hdl-next-huddle-bar-n">{q11HasData ? q11Aggregated.totalN : ''}</span>
                                    <span aria-hidden="true" />
                                </div>
                            )}
                        </div>

                        {isRefreshing && (
                            <div className="chart-loading-overlay">
                                <div>Loading...</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default HuddleNextHuddle
